import { useClipboard } from '@calendly/clipboard';
import { useBooleanState, useQueryParams } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { CheckIcon, CopyIcon, DeleteIcon, EditPencilIcon } from '@calendly/ui/components/icons';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { SimplePopup } from '@calendly/ui/components/popup';
import { successColor } from '@calendly/ui/theme';
import classNames from 'classnames';
import { useContext, useEffect, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { BOOKING_METHODS, editPoll } from 'features/one_off_modal';
import { useLinkDomainData } from 'hooks/analytics_events';
import { useTrackBookingPageShared } from 'hooks/analytics_events/useTrackBookingPageShared';
import { useEventTypeLocation } from 'hooks/useEventTypeLocation';
import { MeetingDetailDependenciesContext } from '../meeting_detail_context';
const popupDescriptionCss = "p1048hhw";
const PopupDescription = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'p';
  return <Component ref={ref} {...restProps} className={classNames(popupDescriptionCss, props.className)} />;
});
const CopyLinkButton = ({
  event
}) => {
  const {
    bookingUrl,
    bookingMethod,
    name,
    eventTypeId
  } = event;
  const [Wrapper, ref, isCopied] = useClipboard(bookingUrl);
  const initialLabel = I18n.t('publisher.event_types.context.copy_link');
  const copiedLabel = I18n.t('publisher.event_types.context.copied');
  const domainProps = useLinkDomainData(bookingUrl);
  const locationType = useEventTypeLocation(event);
  const trackBookingPageShared = useTrackBookingPageShared(eventTypeId);
  const sendBookingPageShared = () => {
    const type = bookingMethod === 'poll' ? bookingMethod : 'adhoc_event';
    trackBookingPageShared({
      location_type: locationType || null,
      event_type_name: name,
      share_object: 'event_type',
      share_object_id: 'NULL',
      share_method: 'copy_link',
      share_location: 'pending_events',
      is_single_use: false,
      is_customized_at_share: type === 'adhoc_event',
      ...domainProps
    });
  };
  return <Wrapper>
      <MinimalButton fontSize="small" ref={ref} onClick={sendBookingPageShared}>
        {!isCopied && <>
            <CopyIcon />
            {initialLabel}
          </>}
        {isCopied && <>
            <CheckIcon color={successColor} />
            {copiedLabel}
          </>}
      </MinimalButton>
    </Wrapper>;
};
const EditPollLink = ({
  event
}) => {
  const {
    eventTypeId,
    canEditPoll,
    bookingMethod
  } = event;
  const dispatch = useDispatch();
  if (bookingMethod !== BOOKING_METHODS.poll) return null;
  if (!canEditPoll) return null;
  const openEditModal = () => {
    dispatch(editPoll({
      eventTypeId
    }));
  };
  return <MinimalButton onClick={openEditModal}>
      <EditPencilIcon />
      {I18n.t('publisher.scheduled_events.pending.edit_poll')}
    </MinimalButton>;
};
export const PendingMeetingActions = ({
  event,
  clearSelectedEvent
}) => {
  const dependencies = useContext(MeetingDetailDependenciesContext);
  if (!dependencies) {
    throw new Error('PendingMeetingActions must be wrapped in a MeetingDetailDependenciesProvider');
  }
  const {
    useDeletePendingEventMutation
  } = dependencies;
  const [isDeleteModalOpen, openDeleteModal, closeDeleteModal] = useBooleanState(false);
  const {
    trigger_delete: deletionEventUUID
  } = useQueryParams();
  const startDeletionOnMount = event.uuid && deletionEventUUID === event?.uuid;
  const [deletePendingEvent, {
    isLoading,
    isSuccess
  }] = useDeletePendingEventMutation({
    fixedCacheKey: 'shared-delete-pending'
  });
  const isExpired = event.expired;
  const isPoll = event.bookingMethod === BOOKING_METHODS.poll;
  useEffect(() => {
    if (startDeletionOnMount) {
      openDeleteModal();
    }
  }, [startDeletionOnMount, openDeleteModal]);
  useEffect(() => {
    if (isSuccess) {
      closeDeleteModal();
      clearSelectedEvent();
    }
  }, [isSuccess, closeDeleteModal, clearSelectedEvent]);
  return <>
      {!isExpired && <CopyLinkButton event={event} />}
      {!isExpired && isPoll && <EditPollLink event={event} />}
      <MinimalButton fontSize="small" decoration="danger" onClick={openDeleteModal}>
        <DeleteIcon />
        {I18n.t('publisher.action.delete')}
      </MinimalButton>
      <SimplePopup isOpen={isDeleteModalOpen} onClose={closeDeleteModal} title={I18n.t('publisher.scheduled_events.pending.delete_title')} cancelLabel={I18n.t('publisher.action.cancel')} confirmLabel={I18n.t('publisher.action.delete')} onConfirm={() => deletePendingEvent(event.pendingId)} isLoading={isLoading}>
        <PopupDescription>
          {I18n.t('publisher.scheduled_events.pending.delete_description')}
        </PopupDescription>
      </SimplePopup>
    </>;
};

require("./pending_meeting_actions.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./pending_meeting_actions.js");